/**
 * Rate Limiter Utility
 * Client-side rate limiting helper for admin operations
 * Provides feedback on rate limit violations
 */

/**
 * Rate limit configuration
 */
const RATE_LIMITS = {
  email_lookup: { max: 10, window: 60000 }, // 10 per minute
  admin_operation: { max: 100, window: 60000 }, // 100 per minute
  role_change: { max: 5, window: 3600000 }, // 5 per hour
};

/**
 * In-memory rate limit tracking (client-side only, not authoritative)
 * This is for UX feedback only - server-side rate limiting is authoritative
 */
const rateLimitStore = new Map();

/**
 * Check if an operation is within rate limits (client-side check)
 * @param {string} operationType - Type of operation
 * @returns {Object} { allowed: boolean, remaining: number, resetAt: Date }
 */
export function checkClientRateLimit(operationType) {
  const config = RATE_LIMITS[operationType];
  if (!config) {
    return { allowed: true, remaining: Infinity, resetAt: null };
  }

  const now = Date.now();
  const key = operationType;
  const record = rateLimitStore.get(key);

  if (!record || now > record.resetAt) {
    // Create new window
    const newRecord = {
      count: 0,
      resetAt: now + config.window,
    };
    rateLimitStore.set(key, newRecord);
    return { allowed: true, remaining: config.max, resetAt: new Date(newRecord.resetAt) };
  }

  const remaining = Math.max(0, config.max - record.count);
  return {
    allowed: record.count < config.max,
    remaining,
    resetAt: new Date(record.resetAt),
  };
}

/**
 * Record an operation attempt (client-side)
 * @param {string} operationType - Type of operation
 * @returns {Object} { allowed: boolean, remaining: number, resetAt: Date }
 */
export function recordOperation(operationType) {
  const config = RATE_LIMITS[operationType];
  if (!config) {
    return { allowed: true, remaining: Infinity, resetAt: null };
  }

  const now = Date.now();
  const key = operationType;
  const record = rateLimitStore.get(key);

  if (!record || now > record.resetAt) {
    // Create new window
    const newRecord = {
      count: 1,
      resetAt: now + config.window,
    };
    rateLimitStore.set(key, newRecord);
    return { allowed: true, remaining: config.max - 1, resetAt: new Date(newRecord.resetAt) };
  }

  record.count += 1;
  rateLimitStore.set(key, record);

  const remaining = Math.max(0, config.max - record.count);
  return {
    allowed: record.count <= config.max,
    remaining,
    resetAt: new Date(record.resetAt),
  };
}

/**
 * Get rate limit status for an operation
 * @param {string} operationType - Type of operation
 * @returns {Object} Rate limit status
 */
export function getRateLimitStatus(operationType) {
  const config = RATE_LIMITS[operationType];
  if (!config) {
    return { max: Infinity, current: 0, remaining: Infinity, resetAt: null };
  }

  const check = checkClientRateLimit(operationType);
  const record = rateLimitStore.get(operationType);

  return {
    max: config.max,
    current: record ? record.count : 0,
    remaining: check.remaining,
    resetAt: check.resetAt,
    windowMinutes: config.window / 60000,
  };
}

/**
 * Reset rate limit for an operation (for testing or manual reset)
 * @param {string} operationType - Type of operation
 */
export function resetRateLimit(operationType) {
  rateLimitStore.delete(operationType);
}

/**
 * Reset all rate limits
 */
export function resetAllRateLimits() {
  rateLimitStore.clear();
}

/**
 * Format rate limit error message
 * @param {string} operationType - Type of operation
 * @param {Date} resetAt - When the rate limit resets
 * @returns {string} Formatted error message
 */
export function formatRateLimitError(operationType, resetAt) {
  const config = RATE_LIMITS[operationType];
  if (!config) {
    return 'Rate limit exceeded. Please try again later.';
  }

  const now = Date.now();
  const resetTime = resetAt ? resetAt.getTime() : now + config.window;
  const secondsUntilReset = Math.ceil((resetTime - now) / 1000);
  const minutesUntilReset = Math.ceil(secondsUntilReset / 60);

  if (minutesUntilReset <= 1) {
    return `Rate limit exceeded. Please wait ${secondsUntilReset} seconds before trying again.`;
  }

  return `Rate limit exceeded (${config.max} per ${config.window / 60000} minute(s)). Please wait ${minutesUntilReset} minute(s) before trying again.`;
}

/**
 * Check if operation should be allowed based on client-side rate limit
 * This is for UX only - server will enforce actual limits
 * @param {string} operationType - Type of operation
 * @returns {Object} { allowed: boolean, error?: string }
 */
export function shouldAllowOperation(operationType) {
  const check = checkClientRateLimit(operationType);
  
  if (!check.allowed) {
    return {
      allowed: false,
      error: formatRateLimitError(operationType, check.resetAt),
    };
  }

  return { allowed: true };
}

export default {
  checkClientRateLimit,
  recordOperation,
  getRateLimitStatus,
  resetRateLimit,
  resetAllRateLimits,
  formatRateLimitError,
  shouldAllowOperation,
  RATE_LIMITS,
};
